import React, { useState } from "react";
import { Card } from "../../misc/Card";
import Music from "./Music";
import FontSelector from "./FontSelector";
import CorporateHeadshotField from "./CorporateHeadshotField";
import SlideshowTemplateCard from "./SlideshowTemplateCard";
import SlideshowLogoSelector from "./SlideshowLogoSelector";
import SlideshowOverlaySection from "./SlideshowOverlaySection";
import BrandColorSelector from "../../misc/custom-posts/BrandColorSelector";
import {
    BrandColors,
    ColorSource,
} from "../../misc/custom-posts/CaptionedVideoMediaStep";
import { MusicType } from "./types";
import { ListingType } from "../../../types/userListing";
import { AutoVideoLogoRef } from "../../../types/slideshow";
import {
    KEN_BURNS_MAX_IMAGES,
    SLIDESHOW_TEMPLATES,
    SlideshowTemplateId,
    isKenBurnsTemplate,
    isVideo1Template,
    isPortraitTemplate,
} from "./slideshowTemplates";

interface SlideshowBrandingProps {
    selectedTemplate: SlideshowTemplateId;
    selectedImageCount: number;
    setSelectedTemplate: (id: SlideshowTemplateId) => void;
    /** A motion template was clicked while more photos than it allows are
     *  selected; the card itself is disabled, this lets the caller explain. */
    onBlockedTemplateSelect: () => void;
    colorSource: ColorSource | undefined;
    setColorSource: (source: ColorSource) => void;
    setBrandColors: (colors: BrandColors) => void;
    selectedFont: string;
    setSelectedFont: (font: string) => void;
    selectedLogo: string | null;
    // MB-1366: the user's saved Listing Video Settings logo — the picker
    // restores it (ref -> concrete logo) exactly as the settings page does.
    selectedLogoReference?: AutoVideoLogoRef | null;
    setSelectedLogo: (url: string | null) => void;
    // Classic (Video 1) reusable address overlay (asset id).
    listingId: string;
    listingType: ListingType;
    selectedOverlay: string | null;
    setSelectedOverlay: (id: string | null) => void;
    onSelectedOverlayUrlChange?: (url: string | null) => void;
    // Live overlay-preview inputs (Classic create form).
    formattedAddress?: string;
    sampleImage?: string;
    // When creating an address overlay, the form takes over this step and the
    // other branding sections are hidden.
    creatingOverlay: boolean;
    setCreatingOverlay: (creating: boolean) => void;
    includeMusic: boolean;
    setIncludeMusic: (value: boolean) => void;
    music: MusicType[];
    setMusic: (value: MusicType[]) => void;
    selectedMusic: string;
    setSelectedMusic: (value: string) => void;
    /** Per-field validation errors, shown only after a failed Continue. */
    errors?: { logo?: boolean; headshot?: boolean; music?: boolean };
}

const FieldError: React.FC<{ message: string }> = ({ message }) => (
    <p className="text-red-500 text-xs mt-1">{message}</p>
);

// Stable no-op: Music lists `setLoading` in its fetch-effect deps, so passing a
// fresh inline function each render would re-fire the fetch in a loop.
const noop = (): void => {};

// Combined step: template + branding + audio. Templates use the ZapCap-style
// card grid (3 per row). Brand colors reuse the shared selector (brokerage
// palette or custom); the brokerage + colors it resolves feed the Logo picker.
// The Corporate Headshot becomes the slideshow's "person", and music is the
// existing slideshow track picker.
const SlideshowBranding: React.FC<SlideshowBrandingProps> = ({
    selectedTemplate,
    selectedImageCount,
    setSelectedTemplate,
    onBlockedTemplateSelect,
    colorSource,
    setColorSource,
    setBrandColors,
    selectedFont,
    setSelectedFont,
    selectedLogo,
    setSelectedLogo,
    selectedLogoReference,
    listingId,
    listingType,
    selectedOverlay,
    setSelectedOverlay,
    onSelectedOverlayUrlChange,
    formattedAddress,
    sampleImage,
    creatingOverlay,
    setCreatingOverlay,
    includeMusic,
    setIncludeMusic,
    music,
    setMusic,
    selectedMusic,
    setSelectedMusic,
    errors,
}) => {
    // The user's brokerage (from Brand Colors) seeds the Logo picker's default
    // brokerage; the picker has its own dropdown to switch from there.
    const [brokerageLabel, setBrokerageLabel] = useState<string | undefined>(
        undefined
    );

    // The Classic (Video 1) flow has no text/color/font overlays, so those
    // controls are hidden; only an optional logo and music apply.
    const isVideo1 = isVideo1Template(selectedTemplate);

    // While creating an overlay, the form takes over the whole step as a
    // flex column (scrollable body + pinned footer), so it renders outside the
    // branding Card.
    if (creatingOverlay) {
        return (
            <SlideshowOverlaySection
                listingId={listingId}
                listingType={listingType}
                selectedOverlay={selectedOverlay}
                setSelectedOverlay={setSelectedOverlay}
                onSelectedOverlayUrlChange={onSelectedOverlayUrlChange}
                formattedAddress={formattedAddress}
                sampleImage={sampleImage}
                creating={creatingOverlay}
                setCreating={setCreatingOverlay}
            />
        );
    }

    return (
        <Card>
            <div className="space-y-6 p-4">
                {!creatingOverlay && (
                <div className="space-y-3">
                    <div>
                        <h3 className="text-lg font-semibold text-primary">
                            Template
                        </h3>
                        <p className="text-sm text-neutral-500">
                            Preview each style and pick the one that fits this
                            listing.
                            {selectedImageCount > KEN_BURNS_MAX_IMAGES && (
                                <>
                                    {" "}
                                    Motion templates support up to{" "}
                                    {KEN_BURNS_MAX_IMAGES} photos. Remove{" "}
                                    {selectedImageCount - KEN_BURNS_MAX_IMAGES}{" "}
                                    to use one.
                                </>
                            )}
                        </p>
                    </div>
                    {/* Mobile-first: 1 column on phones, 2 on small tablets,
                        3 on wider screens. Only `sxl` is a named breakpoint in
                        this repo, so responsiveness uses arbitrary variants. */}
                    <div className="grid grid-cols-1 min-[480px]:grid-cols-2 min-[768px]:grid-cols-3 gap-4">
                        {SLIDESHOW_TEMPLATES.filter(
                            (template) => !template.hidden,
                        ).map((template) => {
                            const blocked =
                                isKenBurnsTemplate(template.id) &&
                                selectedImageCount > KEN_BURNS_MAX_IMAGES;
                            return (
                                <SlideshowTemplateCard
                                    key={template.id}
                                    template={template}
                                    selected={selectedTemplate === template.id}
                                    disabled={blocked}
                                    onSelect={() =>
                                        blocked
                                            ? onBlockedTemplateSelect()
                                            : setSelectedTemplate(template.id)
                                    }
                                />
                            );
                        })}
                    </div>
                    {isPortraitTemplate(selectedTemplate) && (
                        <p className="text-xs text-[#fd7400]">
                            This vertical template works best with portrait
                            photos. Landscape photos may be cropped or
                            letterboxed.
                        </p>
                    )}
                </div>
                )}

                {!isVideo1 && (
                    <div className="border-t border-neutral-200 pt-5">
                        <BrandColorSelector
                            value={colorSource}
                            onChange={setColorSource}
                            onColorsChange={setBrandColors}
                            onBrokerageLabelChange={setBrokerageLabel}
                        />
                    </div>
                )}

                {!isVideo1 && (
                    <div className="border-t border-neutral-200 pt-5">
                        <SlideshowLogoSelector
                            selectedLogo={selectedLogo}
                            setSelectedLogo={setSelectedLogo}
                            defaultBrokerageLabel={brokerageLabel}
                            selectedReference={selectedLogoReference}
                        />
                        {errors?.logo && (
                            <FieldError message="Select a logo for your slideshow." />
                        )}
                    </div>
                )}

                {isVideo1 && (
                    <div
                        className={
                            creatingOverlay
                                ? undefined
                                : "border-t border-neutral-200 pt-5"
                        }
                    >
                        <SlideshowOverlaySection
                            listingId={listingId}
                            listingType={listingType}
                            selectedOverlay={selectedOverlay}
                            setSelectedOverlay={setSelectedOverlay}
                            onSelectedOverlayUrlChange={onSelectedOverlayUrlChange}
                            formattedAddress={formattedAddress}
                            sampleImage={sampleImage}
                            creating={creatingOverlay}
                            setCreating={setCreatingOverlay}
                        />
                    </div>
                )}

                {!isVideo1 && (
                    <div className="border-t border-neutral-200 pt-5">
                        <FontSelector
                            selectedFont={selectedFont}
                            setSelectedFont={setSelectedFont}
                        />
                    </div>
                )}

                {!isVideo1 && (
                    <div className="border-t border-neutral-200 pt-5">
                        <CorporateHeadshotField />
                        {errors?.headshot && (
                            <FieldError message="Add a corporate headshot to use as the person in your slideshow." />
                        )}
                    </div>
                )}

                {!creatingOverlay && (
                <div className="border-t border-neutral-200 pt-5">
                    <Music
                        includeMusic={includeMusic}
                        setIncludeMusic={setIncludeMusic}
                        setLoading={noop}
                        music={music}
                        setMusic={setMusic}
                        selectedMusic={selectedMusic}
                        setSelectedMusic={setSelectedMusic}
                    />
                    {errors?.music && (
                        <FieldError message="Choose a music track, or turn music off." />
                    )}
                </div>
                )}
            </div>
        </Card>
    );
};

export default SlideshowBranding;
